import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';

interface JuridicaCostsData {
  general_table: any[];
  analysis_table: any[];
}

interface JuridicaCostsViewProps {
  data: JuridicaCostsData;
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000000) {
    return `${(tickItem / 1000000000).toFixed(1)}B`;
  }
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const JuridicaCostsView: React.FC<JuridicaCostsViewProps> = ({ data, title }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!data || !data.analysis_table || data.analysis_table.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>Análisis de costos y gastos deducibles</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="sub-vistas de costos y gastos">
          <Tab label="Gráfica" />
          <Tab label="Tabla General" />
          <Tab label="Total Costos y Gastos" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Total costos y gastos deducibles con Tendencia</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.analysis_table}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Anio" type="category" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO').format(value)} />
              <Legend />
              <Bar dataKey="Total_costos_y_gastos_deducibles" fill={theme.palette.primary.main} name="Total costos y gastos deducibles" />
              <Line type="monotone" dataKey="Total_costos_y_gastos_deducibles" stroke={theme.palette.secondary.main} name="Tendencia" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla general de costos y gastos</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Costos</TableCell>
                  <TableCell>Gastos Operacionales Administración</TableCell>
                  <TableCell>Gastos Distribución Ventas</TableCell>
                  <TableCell>Otros Gastos Deducciones</TableCell>
                  <TableCell>Gastos Financieros</TableCell>
                  <TableCell>Otros Gastos y Deducciones</TableCell>
                  <TableCell>Total Costos y Gastos Deducibles</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.general_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Costos)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Gastos_operacionales_administracion)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Gastos_distribucion_ventas)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Otros_gastos_deducciones)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Gastos_financieros)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Otros_gastos_y_deducciones)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_costos_y_gastos_deducibles)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla de Total_costos_y_gastos_deducibles</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Total costos y gastos deducibles</TableCell>
                  <TableCell>Análisis %</TableCell>
                  <TableCell>Análisis $</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.analysis_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_costos_y_gastos_deducibles)}</TableCell>
                    <TableCell>{row['Analisis %'] ? row['Analisis %'].toFixed(2) : '0.00'}%</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row['Analisis $'])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default JuridicaCostsView;